import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';

import { db } from './firebaseConfig.js';
import { RaceEntryRepository } from '../repositories/RaceEntryRepository.js';
import { RaceEntry } from '../../domain/models/RaceEntry.js';

/**
 * Firebase/Firestore implementation of RaceEntryRepository.
 *
 * Races are stored as an embedded array inside the resume document,
 * so every mutation is a read-modify-write on the parent doc.
 */
export class FirebaseRaceEntryRepo extends RaceEntryRepository {

  /** @returns {Promise<RaceEntry>} */
  async add(resumeId, raceEntry) {
    const docRef = doc(db, 'resumes', resumeId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) throw new Error(`Resume ${resumeId} not found`);

    const data = snap.data();
    const races = data.races ?? [];

    // Assign a unique ID to the new race entry
    const newRace = new RaceEntry({
      ...raceEntry,
      id: raceEntry.id ?? _generateId(),
    });

    races.push(newRace.toFirestore());

    await updateDoc(docRef, {
      races,
      updatedAt: serverTimestamp(),
    });

    return newRace;
  }

  /** @returns {Promise<void>} */
  async update(resumeId, raceId, data) {
    const docRef = doc(db, 'resumes', resumeId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) throw new Error(`Resume ${resumeId} not found`);

    const resumeData = snap.data();
    const races = resumeData.races ?? [];

    const index = races.findIndex((r) => r.id === raceId);
    if (index === -1) throw new Error(`Race ${raceId} not found in resume ${resumeId}`);

    races[index] = { ...races[index], ...data };

    await updateDoc(docRef, {
      races,
      updatedAt: serverTimestamp(),
    });
  }

  /** @returns {Promise<void>} */
  async delete(resumeId, raceId) {
    const docRef = doc(db, 'resumes', resumeId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) throw new Error(`Resume ${resumeId} not found`);

    const data = snap.data();
    const races = (data.races ?? []).filter((r) => r.id !== raceId);

    await updateDoc(docRef, {
      races,
      updatedAt: serverTimestamp(),
    });
  }

  /** @returns {Promise<RaceEntry[]>} */
  async getByResumeId(resumeId) {
    const snap = await getDoc(doc(db, 'resumes', resumeId));
    if (!snap.exists()) throw new Error(`Resume ${resumeId} not found`);

    const data = snap.data();
    return (data.races ?? []).map((r) => RaceEntry.fromFirestore(r));
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Generate a short unique ID for a race entry. */
function _generateId() {
  // crypto.randomUUID is available in modern browsers and Node 19+
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback: base-36 timestamp + random suffix
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}
